import { computed } from 'vue';
import type { Ref } from 'vue';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import type { LevelItem, LevelSource } from './types';

export const explorerVisitDashboardPath = '/visit/dashboard';
export const explorerHomePath = '/home';
export const explorerRandomPath = '/level/random';

const homeRoutes = new Set(['/home', '/']);

type ExplorerSceneState = {
  showProfile: Ref<boolean>;
  showEdit: Ref<boolean>;
  activeLevel: Ref<LevelItem | null>;
  activeSource: Ref<LevelSource>;
  messages: Ref<any[]>;
};

type ExplorerSceneActions = {
  requireLogin: () => boolean;
  loadVisitDashboard: () => void;
  loadRandomLevel: () => void;
  loadLevelDetail: (level: { id: number }, onSuccess: (detail: LevelItem) => void) => void;
  openEditWithDetail: (level: LevelItem) => void;
};

export const useLevelExplorerScene = (
  route: RouteLocationNormalizedLoaded,
  router: Router,
  state: ExplorerSceneState,
  actions: ExplorerSceneActions
) => {
  const showHomeRoute = computed(() => homeRoutes.has(route.path));
  const showVisitStats = computed(() => route.path === explorerVisitDashboardPath);
  const showRandomLevel = computed(() => route.path === explorerRandomPath);
  const routeLevelId = computed(() => {
    const id = route.params.id;
    const rawId = Array.isArray(id) ? id[0] : id;
    return Number(rawId || 0);
  });
  const showLevelEditRoute = computed(() => route.path.startsWith('/level/') && route.path.endsWith('/edit'));
  const showLevelDetailRoute = computed(() => route.path.startsWith('/level/') && !showRandomLevel.value && !showLevelEditRoute.value);

  const goHome = () => {
    if (route.path !== explorerHomePath) {
      router.push(explorerHomePath);
    }
  };

  const leaveVisitStatsRoute = () => {
    if (showVisitStats.value) {
      router.push(explorerHomePath);
    }
  };

  const getRouteSource = (): LevelSource => {
    const source = String(route.query.source || 'archive');
    if (source === 'mine' || source === 'favorite' || source === 'random') return source;
    return 'archive';
  };

  const clearActiveLevel = () => {
    state.activeLevel.value = null;
    state.messages.value = [];
    state.activeSource.value = 'archive';
  };

  const syncRouteView = () => {
    if (showVisitStats.value) {
      clearActiveLevel();
      state.showProfile.value = false;
      state.showEdit.value = false;
      actions.loadVisitDashboard();
      return;
    }

    if (showRandomLevel.value) {
      state.showProfile.value = false;
      state.showEdit.value = false;
      actions.loadRandomLevel();
      return;
    }

    if (showLevelEditRoute.value) {
      if (!routeLevelId.value) {
        goHome();
        return;
      }
      if (!actions.requireLogin()) {
        goHome();
        return;
      }
      state.showProfile.value = false;
      actions.loadLevelDetail({ id: routeLevelId.value }, actions.openEditWithDetail);
      return;
    }

    if (showLevelDetailRoute.value) {
      if (!routeLevelId.value) {
        goHome();
        return;
      }
      state.showProfile.value = false;
      state.showEdit.value = false;
      actions.loadLevelDetail({ id: routeLevelId.value }, (detail) => {
        state.activeLevel.value = detail;
        state.activeSource.value = getRouteSource();
        state.messages.value = [];
      });
      return;
    }

    if (showHomeRoute.value) {
      state.showEdit.value = false;
      clearActiveLevel();
    }
  };

  return {
    showHomeRoute,
    showVisitStats,
    showRandomLevel,
    showLevelEditRoute,
    showLevelDetailRoute,
    routeLevelId,
    goHome,
    leaveVisitStatsRoute,
    clearActiveLevel,
    syncRouteView
  };
};
